import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, X, Check } from "lucide-react";
import { api } from "../../lib/auth";

const EMPTY = {
  name: "",
  price_monthly: 0,
  price_yearly: 0,
  features: [],
  limits: { products: 0, users: 0, warehouses: 0 },
  modules: [],
};

function PlanDialog({ open, initial, onClose, onSaved, allModules }) {
  const [form, setForm] = useState(EMPTY);
  const [featInput, setFeatInput] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initial ? {
        name: initial.name || "",
        price_monthly: initial.price_monthly || 0,
        price_yearly: initial.price_yearly || 0,
        features: initial.features || [],
        limits: initial.limits || { products: 0, users: 0, warehouses: 0 },
        modules: initial.modules || [],
      } : EMPTY);
      setFeatInput("");
    }
  }, [open, initial]);

  if (!open) return null;

  const toggleModule = (m) =>
    setForm((f) => ({ ...f, modules: f.modules.includes(m) ? f.modules.filter((x) => x !== m) : [...f.modules, m] }));

  const addFeat = () => {
    const v = featInput.trim();
    if (!v) return;
    setForm((f) => ({ ...f, features: [...f.features, v] }));
    setFeatInput("");
  };
  const removeFeat = (i) =>
    setForm((f) => ({ ...f, features: f.features.filter((_, k) => k !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Plan name required");
    setBusy(true);
    try {
      const payload = {
        ...form,
        price_monthly: Number(form.price_monthly) || 0,
        price_yearly: Number(form.price_yearly) || 0,
        limits: {
          products: Number(form.limits.products) || 0,
          users: Number(form.limits.users) || 0,
          warehouses: Number(form.limits.warehouses) || 0,
        },
      };
      if (initial?.id) {
        await api.patch(`/admin/plans/${initial.id}`, payload);
        toast.success("Plan updated");
      } else {
        await api.post("/admin/plans", payload);
        toast.success("Plan created");
      }
      onSaved();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-black/8 flex items-center justify-between">
          <div>
            <h2 className="font-display font-bold text-xl text-black">{initial ? "Edit Plan" : "Create Plan"}</h2>
            <p className="text-xs text-black/55 mt-0.5">Define pricing, modules and limits for this tier.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-black/5"><X size={18} /></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-black/55 font-bold">Plan name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="plan-name" placeholder="e.g. Pro"
              className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1B4332] outline-none text-sm" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-black/55 font-bold">Monthly (₦)</label>
              <input type="number" min="0" value={form.price_monthly} data-testid="plan-price-m"
                onChange={(e) => setForm({ ...form, price_monthly: e.target.value })}
                className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1B4332] outline-none text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-black/55 font-bold">Yearly (₦)</label>
              <input type="number" min="0" value={form.price_yearly} data-testid="plan-price-y"
                onChange={(e) => setForm({ ...form, price_yearly: e.target.value })}
                className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1B4332] outline-none text-sm" />
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-black/55 font-bold mb-2">Included modules</div>
            <div className="grid grid-cols-2 gap-2">
              {allModules.map((m) => {
                const on = form.modules.includes(m);
                return (
                  <label key={m} data-testid={`plan-module-${m}`}
                    className={`flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border cursor-pointer text-sm ${
                      on ? "bg-[#F4F8F3] border-[#1B4332]/30 text-[#1B4332]" : "bg-white border-black/10 text-black/70 hover:border-black/25"
                    }`}>
                    <span className="font-semibold truncate">{m}</span>
                    <input type="checkbox" checked={on} onChange={() => toggleModule(m)} className="w-4 h-4 accent-[#1B4332] flex-shrink-0" />
                  </label>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field lbl="Products limit" value={form.limits.products} testid="plan-limit-products"
              onChange={(v) => setForm({ ...form, limits: { ...form.limits, products: v } })} hint="-1 = unlimited" />
            <Field lbl="Users limit" value={form.limits.users} testid="plan-limit-users"
              onChange={(v) => setForm({ ...form, limits: { ...form.limits, users: v } })} hint="-1 = unlimited" />
            <Field lbl="Warehouses" value={form.limits.warehouses} testid="plan-limit-warehouses"
              onChange={(v) => setForm({ ...form, limits: { ...form.limits, warehouses: v } })} hint="-1 = unlimited" />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-black/55 font-bold mb-2">Marketing features</div>
            <div className="flex gap-2">
              <input value={featInput} onChange={(e) => setFeatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFeat(); } }}
                data-testid="plan-feat-input" placeholder="Add a bullet (e.g. 500 Products)"
                className="flex-1 px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1B4332] outline-none text-sm" />
              <button type="button" onClick={addFeat} data-testid="plan-feat-add"
                className="px-3 rounded-lg border border-black/15 text-sm font-semibold hover:bg-black/5">Add</button>
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {form.features.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F4F8F3] text-[#1B4332] text-xs font-medium">
                  {f}
                  <button type="button" onClick={() => removeFeat(i)} className="hover:text-red-600"><X size={10} /></button>
                </span>
              ))}
              {form.features.length === 0 && <div className="text-xs text-black/40">No features yet.</div>}
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-black/8 pt-4">
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 rounded-lg border border-black/15 text-sm font-semibold">Cancel</button>
            <button type="submit" disabled={busy} data-testid="plan-submit"
              className="px-4 py-2.5 rounded-lg bg-[#1B4332] text-white text-sm font-semibold hover:bg-[#133124] disabled:opacity-60">
              {busy ? "Saving..." : (initial ? "Save changes" : "Create plan")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ lbl, value, onChange, testid, hint }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-black/55 font-bold">{lbl}</label>
      <input type="number" value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-black/15 focus:border-[#1B4332] outline-none text-sm" />
      {hint && <div className="text-[10px] text-black/45 mt-1">{hint}</div>}
    </div>
  );
}

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [allModules, setAllModules] = useState([]);
  const [dlg, setDlg] = useState({ open: false, initial: null });

  const load = async () => {
    const r = await api.get("/plans");
    setPlans(r.data);
  };
  useEffect(() => {
    load();
    api.get("/admin/modules-catalog").then((r) => setAllModules(r.data.modules)).catch(() => {});
  }, []);

  const remove = async (p) => {
    if (!window.confirm(`Delete plan "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/plans/${p.id}`);
      toast.success("Plan deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-plans-page" className="p-8 lg:p-10 max-w-[1400px]">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-extrabold text-4xl text-black tracking-tight">Plans</h1>
          <p className="text-black/60 mt-1.5 text-sm">Subscription tiers shown to tenants during sign-up.</p>
        </div>
        <button onClick={() => setDlg({ open: true, initial: null })} data-testid="plans-new-button"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#1B4332] text-white text-sm font-semibold hover:bg-[#133124]">
          <Plus size={14} /> New Plan
        </button>
      </div>

      <div className="mt-7 grid md:grid-cols-2 xl:grid-cols-4 gap-5">
        {plans.map((p) => {
          const featured = p.id === "plan_pro";
          return (
            <div key={p.id} data-testid={`plan-card-${p.id}`}
              className={`rounded-2xl p-6 border flex flex-col ${
                featured ? "bg-gradient-to-br from-[#0F2A1F] via-[#1B4332] to-[#2D6A4F] text-white border-[#1B4332]" : "bg-white text-black border-black/8"
              }`}>
              <div className="flex items-start justify-between">
                <h3 className={`font-display font-bold text-xl ${featured ? "text-white" : "text-black"}`}>{p.name}</h3>
                {featured && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-white/20 px-2 py-1 rounded-full uppercase tracking-wider">
                    <Star size={10} /> Popular
                  </span>
                )}
              </div>
              <div className="mt-3">
                <span className={`font-display font-extrabold text-3xl tracking-tight`}>
                  ₦{Number(p.price_monthly).toLocaleString()}
                </span>
                <span className={`text-sm ${featured ? "text-white/70" : "text-black/50"}`}>/mo</span>
              </div>
              <div className={`text-xs ${featured ? "text-white/60" : "text-black/50"}`}>
                or ₦{Number(p.price_yearly).toLocaleString()}/yr
              </div>

              {p.modules?.length > 0 && (
                <div className="mt-4">
                  <div className={`text-[10px] uppercase tracking-wider font-bold ${featured ? "text-white/60" : "text-black/45"}`}>Modules</div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {p.modules.map((m) => (
                      <span key={m} className={`text-[10px] px-2 py-0.5 rounded-full ${featured ? "bg-white/15 text-white/90" : "bg-[#F4F8F3] text-[#1B4332]"}`}>{m}</span>
                    ))}
                  </div>
                </div>
              )}

              <ul className="mt-4 space-y-1.5 flex-1">
                {(p.features || []).slice(0, 6).map((f) => (
                  <li key={f} className={`flex items-start gap-2 text-sm ${featured ? "text-white/85" : "text-black/75"}`}>
                    <Check size={13} className={`mt-1 flex-shrink-0 ${featured ? "text-white/70" : "text-[#1FA868]"}`} /> {f}
                  </li>
                ))}
              </ul>

              <div className="mt-5 pt-4 border-t border-black/10 flex gap-2">
                <button onClick={() => setDlg({ open: true, initial: p })} data-testid={`plan-edit-${p.id}`}
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold ${
                    featured ? "bg-white/15 text-white hover:bg-white/25" : "bg-[#F4F8F3] text-[#1B4332] hover:bg-[#E3EEE2]"
                  }`}>
                  <Pencil size={12} /> Edit
                </button>
                <button onClick={() => remove(p)} data-testid={`plan-delete-${p.id}`}
                  className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold ${
                    featured ? "bg-rose-500/80 text-white hover:bg-rose-500" : "bg-rose-50 text-rose-600 hover:bg-rose-100"
                  }`}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          );
        })}
        {plans.length === 0 && <div className="col-span-full py-10 text-center text-black/50">No plans yet.</div>}
      </div>

      <PlanDialog open={dlg.open} initial={dlg.initial} allModules={allModules}
        onClose={() => setDlg({ open: false, initial: null })} onSaved={load} />
    </div>
  );
}
